import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Laptop, Loader2, Smartphone } from "lucide-react";
import { toast } from "sonner";

import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { authSession } from "@/core/api/authSession";
import type { DeviceSession } from "../../domain/entities/DeviceSession";
import {
  useGetSessionsQuery,
  useRevokeSessionMutation,
} from "../../infrastructure/api/sessionsApi";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface DeviceRowProps {
  session: DeviceSession;
  onLoggedOut: (session: DeviceSession) => void;
}

const DeviceRow: React.FC<DeviceRowProps> = ({ session, onLoggedOut }) => {
  const { t } = useTranslation("settings");
  const [revokeSession, { isLoading }] = useRevokeSessionMutation();
  const Icon = session.deviceKind === "mobile" ? Smartphone : Laptop;

  const handleLogout = async () => {
    try {
      await revokeSession(session.id).unwrap();
      onLoggedOut(session);
    } catch {
      toast.error(t("devicesDialog.logoutError"));
    }
  };

  return (
    <div className="flex items-center justify-between gap-4 py-3">
      <div className="flex min-w-0 items-center gap-3">
        <Icon className="h-5 w-5 shrink-0 text-gray-500" />
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-semibold text-gray-900">
              {session.deviceName}
            </span>
            {session.isCurrent && (
              <span className="rounded-full bg-[#EAF2FF] px-2 py-0.5 text-xs font-medium text-primary">
                {t("devicesDialog.currentDevice")}
              </span>
            )}
          </div>
          <p className="mt-0.5 truncate text-xs text-gray-500">
            {session.location} · {session.lastActiveLabel}
          </p>
        </div>
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        loading={isLoading}
        onClick={() => void handleLogout()}
        className="shrink-0 border-[#E53935] text-[#E53935] hover:bg-[#FDECEA] hover:text-[#E53935]"
      >
        {t("devicesDialog.logout")}
      </Button>
    </div>
  );
};

export const ManageDevicesDialog: React.FC<Props> = ({
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation("settings");
  const navigate = useNavigate();
  const {
    data: sessions = [],
    isLoading,
    isError,
  } = useGetSessionsQuery(undefined, { skip: !open });

  const handleLoggedOut = (session: DeviceSession) => {
    if (session.isCurrent) {
      authSession.clearTokens();
      onOpenChange(false);
      navigate("/login", { replace: true });
      return;
    }
    toast.success(
      t("devicesDialog.loggedOutToast", { device: session.deviceName }),
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[calc(100dvh-1rem)] min-h-0 flex-col overflow-hidden rounded-xl sm:max-h-[80vh] sm:max-w-[560px]">
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-lg font-semibold">
            {t("devicesDialog.title")}
          </DialogTitle>
          <p className="text-sm text-gray-500">
            {t("devicesDialog.subtitle")}
          </p>
        </DialogHeader>

        <div className="min-h-0 flex-1 divide-y overflow-y-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-primary" />
            </div>
          ) : isError ? (
            <p className="py-10 text-center text-sm text-red-500">
              {t("devicesDialog.loadError")}
            </p>
          ) : sessions.length === 0 ? (
            <p className="py-10 text-center text-sm text-gray-500">
              {t("devicesDialog.empty")}
            </p>
          ) : (
            sessions.map((session) => (
              <DeviceRow
                key={session.id}
                session={session}
                onLoggedOut={handleLoggedOut}
              />
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
